import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface AssignableRole {
  readonly name: string;
  readonly description?: string | null;
}

/**
 * Assign/replace a user's roles from a checkbox list (PTV2-1451). Purely presentational and
 * plane-agnostic — the host supplies the catalogue + the user's current roles + the save handler — so
 * the same dialog serves global users now and tenant users later. Roles the user already holds that are
 * NOT in the catalogue are still shown, checked, so a save can never silently drop them (mirrors the
 * tenant role dialog).
 */
export function ManageUserRolesDialog({
  open,
  userName,
  assignableRoles,
  currentRoles,
  rolesLoading,
  onOpenChange,
  onSave,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly userName: string;
  readonly assignableRoles: readonly AssignableRole[];
  readonly currentRoles: readonly string[];
  readonly rolesLoading: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSave: (roleNames: readonly string[]) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const fp = 'manageUserRoles';

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  // Reseed the checked set whenever the dialog opens (or targets a different user's roles).
  useEffect(() => {
    if (open) {
      setSelected(new Set(currentRoles));
    }
  }, [open, currentRoles]);

  // Union of the catalogue and the user's current roles, so out-of-catalogue roles stay visible/checked.
  const rows = useMemo(() => {
    const catalogue = new Map<string, AssignableRole>();
    for (const role of assignableRoles) {
      catalogue.set(role.name, role);
    }
    for (const name of currentRoles) {
      if (!catalogue.has(name)) {
        catalogue.set(name, { name });
      }
    }
    return [...catalogue.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [assignableRoles, currentRoles]);

  function toggle(name: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t(`${fp}.title`, { name: userName })}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t(`${fp}.description`)}</p>

        {rolesLoading ? (
          <p className="text-sm text-muted-foreground" role="status">
            {t(`${fp}.loading`)}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" role="status">
            {t(`${fp}.empty`)}
          </p>
        ) : (
          <ul className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border">
            {rows.map((role) => (
              <li key={role.name}>
                <label className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-accent/50">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                    checked={selected.has(role.name)}
                    onChange={() => {
                      toggle(role.name);
                    }}
                  />
                  <span className="min-w-0 flex flex-col leading-tight">
                    <span className="text-sm font-medium text-foreground">{role.name}</span>
                    {role.description !== null && role.description !== undefined && role.description.length > 0 && (
                      <span className="text-xs text-muted-foreground">{role.description}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t(`${fp}.submitError`, { code: getErrorCodeForUi(submitError) })}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={pending}
          >
            {t(`${fp}.cancel`)}
          </Button>
          <Button
            type="button"
            loading={pending}
            onClick={() => {
              onSave([...selected]);
            }}
          >
            {t(`${fp}.save`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
